import { memo, useMemo } from 'react';
import { WELLNESS_QUESTIONNAIRE } from './questionnaire';
import { parseScaleLabels, getMetricKey, getImprovementDisplay } from './wellnessUtils';
import type { WellnessMetricKey } from './wellnessUtils';
import ProgressBar from './ProgressBar';

interface TooltipData {
  pre: number;
  post: number;
  improvementPercentage: number;
}

interface WellnessChartTooltipProps {
  indexValue: string | number;
  data: Partial<TooltipData>;
  getImprovementColor: (metricLabel: string, improvementPercentage: number) => string;
}

const EXPECTED_OUTCOMES: Record<WellnessMetricKey, string> = {
  pleasant: 'We expect emotional readiness to go UP',
  tense: 'We expect tension to go DOWN',
  calm_excited: 'We expect stress to go DOWN',
  energetic: 'We expect physical readiness to go UP',
};

export const WellnessChartTooltip = memo(
  ({ indexValue, data, getImprovementColor }: WellnessChartTooltipProps) => {
    const metricKey = useMemo(() => getMetricKey(indexValue), [indexValue]);
    const questionnaire = useMemo(
      () => (metricKey ? WELLNESS_QUESTIONNAIRE[metricKey] : null),
      [metricKey],
    );
    const metricLabel = useMemo(() => String(indexValue).replace(/\n/g, ' '), [indexValue]);
    const scaleLabels = useMemo(
      () => parseScaleLabels(questionnaire?.scale || ''),
      [questionnaire?.scale],
    );

    const preValue = data.pre || 0;
    const postValue = data.post || 0;
    const hasImprovement = typeof data.improvementPercentage === 'number';
    const improvementColor = useMemo(
      () =>
        hasImprovement
          ? getImprovementColor(metricLabel, data.improvementPercentage!)
          : 'text-gray-600',
      [hasImprovement, metricLabel, data.improvementPercentage],
    );

    const improvementDisplay = useMemo(
      () => (hasImprovement ? getImprovementDisplay(data.improvementPercentage!) : null),
      [hasImprovement, data.improvementPercentage],
    );

    return (
      <div className="relative z-[9999] w-[480px] rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] text-[var(--anl-text)] shadow-lg">
        <div className="border-b border-[var(--anl-border)] px-4 py-3">
          <h3 className="text-sm font-semibold">{metricLabel}</h3>
          {questionnaire && (
            <>
              <p className="mt-1 text-base font-medium leading-relaxed text-[var(--anl-text)]">
                {questionnaire.question}
              </p>
              {metricKey && EXPECTED_OUTCOMES[metricKey] && (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--anl-text-muted)]">
                    Expected outcome
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-[var(--anl-text)]">
                    {EXPECTED_OUTCOMES[metricKey]}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="space-y-2 p-4">
          <div className="space-y-3">
            <ProgressBar
              label="Pre-Session"
              value={preValue}
              color="blue"
              scaleLabels={scaleLabels}
            />
            <ProgressBar
              label="Post-Session"
              value={postValue}
              color="green"
              scaleLabels={scaleLabels}
            />
          </div>

          {hasImprovement && (
            <div className="rounded-md border-t border-[var(--anl-border)] bg-[var(--anl-surface)] p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Overall Change</span>
                {improvementDisplay && (
                  <div
                    className={`text-lg font-bold ${improvementColor} flex items-center space-x-1`}
                  >
                    <span>
                      {improvementDisplay.sign}
                      {improvementDisplay.value}%
                    </span>
                    <span className="text-xs">{improvementDisplay.arrow}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <p className="mt-1 text-xs italic text-[var(--anl-text-muted)]">
            Average results for this question
          </p>
        </div>
      </div>
    );
  },
);
